/**
 * Color palettes for Highcharts chart rendering
 * Pure functions - no Vue or Highcharts dependencies
 */

import { operatorBrandColors } from '@/styles/theme/presets/profiteia';

const cssVarColor = (varName, alpha = null) =>
  alpha == null ? `rgb(var(${varName}))` : `rgb(var(${varName}) / ${alpha})`;

const readThemeChannels = (varName, seen = new Set()) => {
  if (typeof document === 'undefined' || seen.has(varName)) return '';

  seen.add(varName);
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  const alias = raw.match(/^var\((--[^),\s]+)\)$/);

  return alias ? readThemeChannels(alias[1], seen) : raw;
};

export const resolveThemeColor = (varName, { alpha = null, fallback = 'transparent' } = {}) => {
  const raw = readThemeChannels(varName);
  if (!raw) return fallback;
  // Netbeheerder brand CSS-tokens (`--color-operator-*`) hold a fixed hex value rather
  // than RGB channels, so `token:operator-tennet` in a catalog colour resolves too.
  if (raw.startsWith('#')) return raw;

  const channels = raw.split(/\s+/).join(', ');
  return alpha == null ? `rgb(${channels})` : `rgba(${channels}, ${alpha})`;
};

/**
 * Get the full Highcharts color palette for chart rendering
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {object} Color palette object
 */
export function getChartColors(isDark) {
  return {
    background: isDark ? cssVarColor('--color-chart-bg') : 'transparent',
    // Tooltip colors must be CONCRETE rgb() values: SVG-mode tooltips (charts
    // without useHTML) apply them as fill/stroke attributes, where CSS var()
    // does not resolve and silently falls back to black-on-black.
    text: resolveThemeColor('--color-chart-tooltip-text'),
    textSecondary: cssVarColor('--color-text-secondary'),
    gridLine: cssVarColor('--color-chart-grid', isDark ? 0.5 : 0.58),
    tooltipBg: resolveThemeColor('--color-chart-tooltip-bg'),
    tooltipBorder: resolveThemeColor('--color-chart-tooltip-border'),
    resetZoom: {
      fill: cssVarColor('--color-surface-base'),
      stroke: cssVarColor('--color-border-default'),
      text: cssVarColor('--color-text-primary'),
      hoverFill: cssVarColor('--color-surface-raised'),
      hoverText: cssVarColor('--color-text-primary'),
      selectFill: cssVarColor('--color-surface-elevated'),
      selectText: cssVarColor('--color-text-primary'),
    },
  };
}

/**
 * Get axis-specific colors for Highcharts y-axis configuration
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {object} Axis color palette
 */
export function getAxisColors(isDark) {
  return {
    text: cssVarColor('--color-chart-axis'),
    gridLine: cssVarColor('--color-chart-grid', isDark ? 0.5 : 0.58),
    line: cssVarColor('--color-chart-grid', isDark ? 0.55 : 0.7),
    // PI-432: the zero line must read as distinct from an ordinary gridline
    // without becoming a series-coloured feature of its own.
    zeroLine: cssVarColor('--color-chart-grid', isDark ? 0.95 : 1),
    indicatorPaneBackground: cssVarColor('--color-surface-raised', isDark ? 0.45 : 0.85),
    indicatorGridLine: cssVarColor('--color-chart-grid', isDark ? 0.45 : 0.5),
    indicatorLine: cssVarColor('--color-chart-grid', isDark ? 0.55 : 0.65),
  };
}

/**
 * Color families for overlay and oscillator indicators.
 * Overlays use warm colors (closer to the main series), oscillators use cool colors.
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {object} { overlay: string[], oscillator: string[] }
 */
export function getIndicatorColorFamilies(isDark) {
  return {
    overlay: isDark
      ? ['rgb(var(--color-state-warning))', 'rgb(var(--color-palette-orange))', 'rgb(var(--color-state-danger))', 'rgb(var(--color-palette-pink))', 'rgb(var(--color-palette-fuchsia))', 'rgb(var(--color-palette-purple))']
      : ['rgb(var(--color-state-warning))', 'rgb(var(--color-palette-orange))', 'rgb(var(--color-state-danger))', 'rgb(var(--color-palette-rose))', 'rgb(var(--color-palette-fuchsia))', 'rgb(var(--color-palette-purple))'],
    oscillator: isDark
      ? ['rgb(var(--color-palette-cyan))', 'rgb(var(--color-brand-accent))', 'rgb(var(--color-palette-indigo))', 'rgb(var(--color-palette-purple))', 'rgb(var(--color-brand-accent))', 'rgb(var(--color-brand-primary))']
      : ['rgb(var(--color-palette-cyan))', 'rgb(var(--color-palette-sky))', 'rgb(var(--color-palette-indigo))', 'rgb(var(--color-palette-violet))', 'rgb(var(--color-brand-accent))', 'rgb(var(--color-brand-primary))'],
  };
}

/**
 * Get colors for image export rendering
 * @param {boolean} isDark - Whether dark mode is active
 * @returns {object} Export-specific color palette
 */
// Export colors are resolved from CSS variables to concrete rgb/rgba strings
// before html2canvas runs. This keeps exports centralized without passing
// unresolved CSS variables into the isolated canvas render.
export function getExportColors(isDark) {
  return {
    bgColor: resolveThemeColor('--color-bg-app'),
    cardBgColor: resolveThemeColor('--color-surface-base'),
    titleColor: resolveThemeColor('--color-text-primary'),
    subtitleColor: resolveThemeColor('--color-text-secondary'),
    metaColor: resolveThemeColor('--color-text-muted'),
    borderColor: resolveThemeColor('--color-border-default', { alpha: isDark ? 0.7 : 0.6 }),
    chartBgColor: resolveThemeColor('--color-surface-base'),
    textColor: resolveThemeColor('--color-text-primary'),
    gridColor: resolveThemeColor('--color-chart-grid', { alpha: isDark ? 0.5 : 0.58 }),
    tooltipBg: resolveThemeColor('--color-chart-tooltip-bg'),
    tooltipBorder: resolveThemeColor('--color-chart-tooltip-border'),
  };
}

/**
 * Exact Highcharts default 10-color palette, repeated to 30 entries.
 * Used for bar/column charts only to ensure legend colors always match
 * the rendered bars (explicit assignment survives incremental updates).
 * Line/timeseries charts let Highcharts assign colors naturally.
 */
export const SERIES_COLORS = [
  'rgb(var(--color-palette-indigo))',
  'rgb(var(--color-text-secondary))',
  'rgb(var(--color-palette-lime))',
  'rgb(var(--color-palette-amber))',
  'rgb(var(--color-palette-indigo))',
  'rgb(var(--color-palette-pink))',
  'rgb(var(--color-palette-yellow))',
  'rgb(var(--color-palette-emerald))',
  'rgb(var(--color-palette-red))',
  'rgb(var(--color-brand-primary-soft))',
  'rgb(var(--color-palette-indigo))',
  'rgb(var(--color-text-secondary))',
  'rgb(var(--color-palette-lime))',
  'rgb(var(--color-palette-amber))',
  'rgb(var(--color-palette-indigo))',
  'rgb(var(--color-palette-pink))',
  'rgb(var(--color-palette-yellow))',
  'rgb(var(--color-palette-emerald))',
  'rgb(var(--color-palette-red))',
  'rgb(var(--color-brand-primary-soft))',
  'rgb(var(--color-palette-indigo))',
  'rgb(var(--color-text-secondary))',
  'rgb(var(--color-palette-lime))',
  'rgb(var(--color-palette-amber))',
  'rgb(var(--color-palette-indigo))',
  'rgb(var(--color-palette-pink))',
  'rgb(var(--color-palette-yellow))',
  'rgb(var(--color-palette-emerald))',
  'rgb(var(--color-palette-red))',
  'rgb(var(--color-brand-primary-soft))',
];

const normalizeMarketName = (value) =>
  String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const MARKET_CATEGORY_FAMILIES = {
  forward: 'forward',
  forwards: 'forward',
  forward_hedging: 'forward',
  forward_and_hedging: 'forward',
  hedging: 'forward',
  day_ahead: 'day-ahead',
  dayahead: 'day-ahead',
  spot: 'day-ahead',
  intraday: 'intraday',
  reserve: 'reserve',
  reserve_capacity: 'reserve',
  reserves: 'reserve',
  balancing: 'balancing',
  balancing_energy: 'balancing',
  balancing_energy_imbalance: 'balancing',
  imbalance: 'balancing',
  congestion: 'congestion',
  congestion_redispatch: 'congestion',
  redispatch: 'congestion',
  ex_post: 'settlement',
  ex_post_settlement: 'settlement',
  settlement: 'settlement',
};

const MARKET_SUBCATEGORY_COLORS = {
  'reserve:fcr': 'reserve-fcr',
  'reserve:afrr': 'reserve-afrr',
  'reserve:afrr_up': 'reserve-afrr-up',
  'reserve:afrr_down': 'reserve-afrr-down',
  'reserve:mfrr': 'reserve-mfrr',
  // The theme has carried --color-market-reserve-mfrr-{up,down} all along; without
  // these two keys any directional mFRR series fell through to an arbitrary ramp
  // slot, which is how the PI-549 bands came out as pale washes unrelated to the
  // aFRR bands beside them.
  'reserve:mfrr_up': 'reserve-mfrr-up',
  'reserve:mfrr_down': 'reserve-mfrr-down',
  'balancing:imbalance': 'balancing-imbalance',
  'balancing:afrr': 'balancing-afrr-activation',
  'balancing:afrr_activation': 'balancing-afrr-activation',
  'balancing:mfrr': 'balancing-mfrr-activation',
  'balancing:mfrr_activation': 'balancing-mfrr-activation',
  'intraday:ida1': 'intraday-ida1',
  'intraday:ida_1': 'intraday-ida1',
  'intraday:ida2': 'intraday-ida2',
  'intraday:ida_2': 'intraday-ida2',
  'intraday:ida3': 'intraday-ida3',
  'intraday:ida_3': 'intraday-ida3',
  'intraday:idc': 'intraday-idc',
  'congestion:congestion': 'congestion-congestion',
  'congestion:redispatch': 'congestion-redispatch',
  'congestion:grid_limit': 'congestion-grid-limit',
  'settlement:correction': 'settlement-correction',
  'settlement:fees': 'settlement-fees',
};

const MARKET_SUBCATEGORY_SERIES_SEEDS = {
  'reserve:fcr': 0,
  'reserve:afrr': 2,
  'reserve:afrr_up': 2,
  'reserve:afrr_down': 4,
  'reserve:mfrr': 5,
  // Kept clear of afrr_up (2) and afrr_down (4) so the four product/direction
  // ramps stay distinguishable across the 8 slots.
  'reserve:mfrr_up': 5,
  'reserve:mfrr_down': 7,
  'balancing:imbalance': 0,
  'balancing:afrr': 3,
  'balancing:afrr_activation': 3,
  'balancing:mfrr': 4,
  'balancing:mfrr_activation': 4,
  'intraday:ida1': 0,
  'intraday:ida_1': 0,
  'intraday:ida2': 2,
  'intraday:ida_2': 2,
  'intraday:ida3': 4,
  'intraday:ida_3': 4,
  'intraday:idc': 6,
  'congestion:congestion': 0,
  'congestion:redispatch': 3,
  'congestion:grid_limit': 5,
  'settlement:correction': 0,
  'settlement:fees': 3,
};

const MARKET_SERIES_SLOT_COUNT = 8;
const NAMED_COLOR_ROLES = new Set(['actual', 'forecast', 'band']);

const MARKET_SERIES_SPREAD_OFFSETS = {
  1: [0],
  2: [0, 4],
  3: [0, 3, 6],
  4: [0, 2, 4, 6],
  5: [0, 2, 4, 6, 1],
  6: [0, 2, 4, 6, 1, 5],
  7: [0, 2, 4, 6, 1, 3, 5],
  8: [0, 2, 4, 6, 1, 3, 5, 7],
};

const resolveMarketTokenColor = (tokenName) =>
  resolveThemeColor(`--color-market-${tokenName}`, {
    fallback: cssVarColor(`--color-market-${tokenName}`),
  });

const clampSeriesGroupSize = (count) => {
  const numericCount = Math.round(Number(count));
  if (!Number.isFinite(numericCount) || numericCount < 1) return 1;
  return Math.min(MARKET_SERIES_SLOT_COUNT, numericCount);
};

const resolveSpreadMarketSlot = ({ seed = 0, index = 0, count = 1 } = {}) => {
  const groupSize = clampSeriesGroupSize(count);
  const numericIndex = Math.abs(Math.round(Number(index) || 0));
  const numericSeed = Math.abs(Math.round(Number(seed) || 0));
  const offsets = MARKET_SERIES_SPREAD_OFFSETS[groupSize] || MARKET_SERIES_SPREAD_OFFSETS[8];
  const offset = offsets[numericIndex % groupSize] ?? 0;

  return ((numericSeed + offset) % MARKET_SERIES_SLOT_COUNT) + 1;
};

// ── semantic layer → palette keys ────────────────────────────────────────────
// The catalog now describes series with semantic dimensions instead of
// market_category/market_subcategory. Two of them decide the colour:
//
//   market_stage + information_state -> palette family
//   product (or subject) + direction -> palette variant within the family
//
// The `reserves` stage deliberately covers both procurement and activation, which
// used to be two separate market categories with two separate palettes. Keying the
// family off information_state as well keeps that visual distinction: activated
// energy stays on the balancing palette, procurement stays on the reserve palette.
const DIRECTION_SUFFIXES = { upward: 'up', downward: 'down' };

// Subjects that own a palette token in their own right when no product applies.
const SUBJECT_VARIANTS = new Set(['imbalance', 'congestion', 'redispatch']);

export const getMarketColorFamily = (marketStageOrSemantics, informationState) => {
  const semantics =
    marketStageOrSemantics && typeof marketStageOrSemantics === 'object'
      ? marketStageOrSemantics
      : { market_stage: marketStageOrSemantics, information_state: informationState };
  const stage = normalizeMarketName(semantics.market_stage);
  if (!stage) return '';
  if (stage === 'reserves' && normalizeMarketName(semantics.information_state) === 'activation') {
    return 'balancing';
  }
  return MARKET_CATEGORY_FAMILIES[stage] || '';
};

export const getMarketColorVariant = (semantics = {}) => {
  const product = normalizeMarketName(semantics.product);
  const subject = normalizeMarketName(semantics.subject);
  const base = product || (SUBJECT_VARIANTS.has(subject) ? subject : '');
  if (!base) return '';
  // Activated energy has one token per product, so direction is not appended there.
  if (normalizeMarketName(semantics.information_state) === 'activation') return base;
  const suffix = DIRECTION_SUFFIXES[normalizeMarketName(semantics.direction)];
  return suffix ? `${base}_${suffix}` : base;
};

export const getMarketColorGroupKey = (semantics = {}) => {
  const family = getMarketColorFamily(semantics);
  if (!family) return '';
  const variant = getMarketColorVariant(semantics);
  return variant ? `${family}:${variant}` : family;
};

// Generation carries no market stage; its colour is the technology (wind, gas, …), so the
// same fuel looks the same on every generation chart. A generation series without a
// technology (totals, "other") takes the neutral `other` token.
export const resolveTechnologyColor = (semantics = {}) => {
  if (normalizeMarketName(semantics.subject) !== 'generation') return undefined;
  const technology = normalizeMarketName(semantics.technology) || 'other';
  return resolveMarketTokenColor(`technology-${technology.replace(/_/g, '-')}`);
};

// Filter ids that hold a grid operator. A series split on one of them takes the operator's
// fixed house-style colour on every chart, so Enexis is the same magenta everywhere.
export const OPERATOR_FILTER_IDS = Object.freeze(['organisation']);

/** Fixed colour of a grid operator (`Enexis`, `TenneT`, …), or undefined when unknown. */
export const resolveOperatorColor = (operator) =>
  operatorBrandColors[String(operator ?? '').trim().toLowerCase()] || undefined;

/** Operator colour for a series that `series_split` expanded per grid operator. */
export const resolveOperatorSplitColor = (visualization, series) => {
  if (!OPERATOR_FILTER_IDS.includes(visualization?.series_split?.by_filter)) return undefined;
  if (series?.groupLabel === undefined || series?.groupLabel === null) return undefined;
  return resolveOperatorColor(series.groupLabel);
};

/**
 * Concrete colours for graph annotations (shaded periods and point labels). Highcharts
 * writes these as SVG attributes, where `var()` does not resolve.
 */
export function getAnnotationColors(isDark) {
  return {
    // A tint of the text colour reads as a quiet shade on both the light and the dark card.
    band: resolveThemeColor('--color-text-muted', { alpha: isDark ? 0.16 : 0.14 }),
    bandLabel: resolveThemeColor('--color-text-secondary'),
    line: resolveThemeColor('--color-text-secondary'),
    labelText: resolveThemeColor('--color-text-primary'),
    labelBackground: resolveThemeColor('--color-surface-base', { alpha: 0.92 }),
    labelBorder: resolveThemeColor('--color-border-default'),
  };
}

// `token:<name>` in a binding's `color` names a theme CSS-token (`--color-<name>`), so a
// catalog colour follows light/dark mode instead of pinning one hex value.
export const resolveBindingColor = (color) => {
  if (typeof color !== 'string' || !color.startsWith('token:')) return color || undefined;
  const varName = `--color-${color.slice('token:'.length)}`;
  return resolveThemeColor(varName, { fallback: cssVarColor(varName) });
};

export const resolveMarketSeriesColor = ({
  semantics,
  seriesRole,
  seriesIndex = 0,
  subcategoryIndex = 0,
  subcategoryCount = 0,
} = {}) => {
  const technologyColor = resolveTechnologyColor(semantics || {});
  if (technologyColor) return technologyColor;
  const family = getMarketColorFamily(semantics || {});
  if (!family) return undefined;

  const subcategory = getMarketColorVariant(semantics || {});
  const subcategoryKey = subcategory ? `${family}:${subcategory}` : '';
  const namedColor = MARKET_SUBCATEGORY_COLORS[subcategoryKey];
  const normalizedRole = normalizeMarketName(seriesRole);

  if (namedColor && (subcategoryCount <= 1 || NAMED_COLOR_ROLES.has(normalizedRole))) {
    return resolveMarketTokenColor(namedColor);
  }

  const seed = MARKET_SUBCATEGORY_SERIES_SEEDS[subcategoryKey];
  const slot = resolveSpreadMarketSlot({
    seed: Number.isFinite(seed) ? seed : 0,
    index: Number.isFinite(seed) ? subcategoryIndex : seriesIndex,
    count: subcategoryCount,
  });
  return resolveMarketTokenColor(`${family}-series-${slot}`);
};

// --- Shade spreading ---------------------------------------------------------
// When several series resolve to the SAME market colour (e.g. many bands of one
// subcategory, or >8 series wrapping the slot ramp), the graph builder calls
// this to fan them into wide, instantly-distinguishable shades anchored on that
// colour — varying lightness AND hue, interleaved so adjacent series are far
// apart. Pure + deterministic so it's unit-testable and stable across renders.
const parseColorChannels = (color) => {
  if (!color || typeof color !== 'string') return null;
  const hex = color.trim().match(/^#([0-9a-f]{6})$/i);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const m = color.match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
  return m ? [Math.round(+m[1]), Math.round(+m[2]), Math.round(+m[3])] : null;
};
const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b); const min = Math.min(r, g, b); const d = max - min;
  let h = 0;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return [h, s, l];
};
const hslToRgbString = (h, s, l) => {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s; const x = c * (1 - Math.abs(((h / 60) % 2) - 1)); const m = l - c / 2;
  let r = 0; let g = 0; let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return `rgb(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)})`;
};
const srgbToLinear = (c) => {
  c /= 255;
  return c > 0.04045 ? ((c + 0.055) / 1.055) ** 2.4 : c / 12.92;
};
const rgbToLab = (r, g, b) => {
  const R = srgbToLinear(r); const G = srgbToLinear(g); const B = srgbToLinear(b);
  const x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  const z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x); const fy = f(y); const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
};
const deltaE = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
const hslStringToRgbTriplet = (h, s, l) => parseColorChannels(hslToRgbString(h, s, l));

export const spreadColorShades = (baseColor, count) => {
  const n = Math.max(1, Math.round(Number(count) || 1));
  const rgb = parseColorChannels(baseColor);
  if (!rgb || n === 1) return rgb ? [baseColor] : [];
  const [h0, s, l0] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const sat = Math.min(0.92, Math.max(0.58, s));
  // Candidate gamut: a wide hue arc around the base + a wide readable lightness
  // band. Greedily pick the colour furthest (max-min ΔE) from those already
  // chosen, anchored on the base, so EVERY pair is distinct, not just neighbours.
  const candidates = [];
  for (let dh = -42; dh <= 42; dh += 7) {
    for (let l = 0.28; l <= 0.78; l += 0.07) {
      const str = hslToRgbString(h0 + dh, sat, l);
      candidates.push({ str, lab: rgbToLab(...hslStringToRgbTriplet(h0 + dh, sat, l)) });
    }
  }
  const baseStr = hslToRgbString(h0, sat, l0);
  const chosen = [{ str: baseStr, lab: rgbToLab(...parseColorChannels(baseStr)) }];
  while (chosen.length < n && candidates.length) {
    let best = -1; let bestD = -1;
    for (let i = 0; i < candidates.length; i += 1) {
      let minD = Infinity;
      for (let j = 0; j < chosen.length; j += 1) {
        const d = deltaE(candidates[i].lab, chosen[j].lab);
        if (d < minD) minD = d;
      }
      if (minD > bestD) { bestD = minD; best = i; }
    }
    chosen.push(candidates[best]);
    candidates.splice(best, 1);
  }
  return chosen.slice(0, n).map((c) => c.str);
};

/**
 * Pick `seriesCount` colors evenly spread across the full SERIES_COLORS range,
 * so the first series always gets the first color and the last always gets
 * the last color, regardless of how many series there are.
 *
 * Examples:
 *   1 series  → [SERIES_COLORS[0]]
 *   2 series  → [SERIES_COLORS[0], SERIES_COLORS[29]]
 *   5 series  → indices 0, 7, 14, 22, 29
 *  30 series  → every color in order
 *
 * @param {number} seriesCount
 * @returns {string[]}
 */
export function getSeriesColorPalette(seriesCount) {
  return Array.from({ length: seriesCount }, (_, i) => {
    return SERIES_COLORS[i];
  });
}
